import { RadioGroup, RadioOption } from '@distribusion/owl-frontend-ui'
import React, { ReactElement } from 'react'

import bem from '@lib/bem'
import { useTranslation } from '@lib/i18n'
import { InteractiveTooltip } from '@ui'

export enum DirectionType {
  OneWay = 'oneWay',
  Return = 'return',
  Weekly = 'weekly',
  OpenReturn = 'openReturn',
}

export interface TripDirectionProps {
  value: DirectionType
  layout: Direction
  oneWayLabel?: string
  twoWayLabel?: string
  weeklyLabel?: string
  openReturnLabel?: string
  twoWay?: boolean
  weekly?: boolean
  isOpenReturn?: boolean
  dataTags?: {
    oneWay?: string
    return?: string
    weekly?: string
    openReturn?: string
  }
  onChange: (value: DirectionType) => void
}

interface TripDirectionOption extends RadioOption<DirectionType> {
  info?: string
}

const TripDirection = (props: TripDirectionProps): ReactElement => {
  const {
    value,
    layout,
    oneWayLabel,
    twoWayLabel,
    weeklyLabel,
    openReturnLabel,
    weekly,
    twoWay = true,
    isOpenReturn,
    onChange,
  } = props
  const { t } = useTranslation()

  const types: TripDirectionOption[] = [
    {
      label: oneWayLabel ?? t('components.direction.oneWay'),
      value: DirectionType.OneWay,
      centered: true,
    },
    ...(weekly
      ? [
          {
            label: weeklyLabel ?? t('components.direction.weekly'),
            value: DirectionType.Weekly,
            centered: true,
          },
        ]
      : []),
    ...(twoWay
      ? [
          {
            label: twoWayLabel ?? t('components.direction.return'),
            value: DirectionType.Return,
            centered: true,
          },
        ]
      : []),
    ...(isOpenReturn
      ? [
          {
            label: openReturnLabel ?? t('components.direction.openReturn'),
            value: DirectionType.OpenReturn,
            centered: true,
            info: t('components.direction.openReturnInfo'),
          },
        ]
      : []),
  ]

  const renderRadioElementWithInfo = (option: TripDirectionOption, radioElement: ReactElement) => {
    if (option.info) {
      return (
        <div className={'ui-radio-wrapper'} key={String(option.value)}>
          {radioElement}
          <InteractiveTooltip
            content={option.info}
            popperClassName={bem('search-form', 'openReturn-tooltip')}
            ariaLabel={t('accessibility.moreInformation')}
          />
        </div>
      )
    }

    return radioElement
  }

  return (
    <RadioGroup
      direction={layout}
      value={value}
      options={types}
      renderRadio={renderRadioElementWithInfo}
      dataTags={props.dataTags}
      onChange={onChange}
    />
  )
}

export default TripDirection
